import { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { bootstrapRefresh, setAccessToken } from '@/lib/api';
import type { AuthRefreshedDetail } from '@/lib/api';

/*
 * AuthContext (Sessio 5b).
 *
 * Yhdistaa apiFetch:n refresh-flow:n ja React-puun. Vastuut:
 *  1. Bootstrap mountilla: kutsu POST /auth/refresh -> jos cookie kelpaa,
 *     palautetaan istunto ilman uutta kirjautumista.
 *  2. Kuuntele apiFetch:n dispatchaamia eventeja ('auth:refreshed' /
 *     'auth:unauthorized') -> paivita tila keskitetysti.
 *  3. Tarjoa setSession / clearSession -funktiot login/logout-mutaatioille.
 *
 * Kontekstin tila:
 *   status:
 *     - 'loading' alussa (bootstrap kesken)
 *     - 'authenticated' kun user+org tunnetaan
 *     - 'unauthenticated' kun istunto puuttuu / on vanhentunut
 *
 * ProtectedRoute kayttaa status:n perusteella:
 *   - loading -> spinner
 *   - authenticated -> renderoi sisalto
 *   - unauthenticated -> redirect /login
 */

export type AuthUser = AuthRefreshedDetail['user'];
export type AuthOrganization = AuthRefreshedDetail['organization'];

export type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated';

export type AuthContextValue = {
  status: AuthStatus;
  user: AuthUser | null;
  organization: AuthOrganization | null;
  /** Login/register-mutaatio kutsuu kun istunto on luotu. */
  setSession: (user: AuthUser, organization: AuthOrganization) => void;
  /** Logout-mutaatio kutsuu kun istunto on suljettu. */
  clearSession: () => void;
};

// eslint-disable-next-line react-refresh/only-export-components
export const AuthContext = createContext<AuthContextValue | null>(null);

type AuthProviderProps = {
  children: ReactNode;
  /** Vain testeissa: ohittaa bootstrap-refreshin. */
  skipBootstrap?: boolean;
};

export function AuthProvider({ children, skipBootstrap = false }: AuthProviderProps) {
  const [status, setStatus] = useState<AuthStatus>(skipBootstrap ? 'unauthenticated' : 'loading');
  const [user, setUser] = useState<AuthUser | null>(null);
  const [organization, setOrganization] = useState<AuthOrganization | null>(null);

  const setSession = useCallback((nextUser: AuthUser, nextOrg: AuthOrganization) => {
    setUser(nextUser);
    setOrganization(nextOrg);
    setStatus('authenticated');
  }, []);

  const clearSession = useCallback(() => {
    setUser(null);
    setOrganization(null);
    setStatus('unauthenticated');
    setAccessToken(null);
  }, []);

  useEffect(() => {
    if (skipBootstrap) return;
    let cancelled = false;
    void (async () => {
      const detail = await bootstrapRefresh();
      if (cancelled) return;
      if (detail) {
        setUser(detail.user);
        setOrganization(detail.organization);
        setStatus('authenticated');
      } else {
        setStatus('unauthenticated');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [skipBootstrap]);

  useEffect(() => {
    const onRefreshed = (event: Event) => {
      const detail = (event as CustomEvent<AuthRefreshedDetail>).detail;
      if (!detail) return;
      setUser(detail.user);
      setOrganization(detail.organization);
      setStatus('authenticated');
    };
    const onUnauthorized = () => {
      setUser(null);
      setOrganization(null);
      setStatus('unauthenticated');
      setAccessToken(null);
    };
    window.addEventListener('auth:refreshed', onRefreshed);
    window.addEventListener('auth:unauthorized', onUnauthorized);
    return () => {
      window.removeEventListener('auth:refreshed', onRefreshed);
      window.removeEventListener('auth:unauthorized', onUnauthorized);
    };
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ status, user, organization, setSession, clearSession }),
    [status, user, organization, setSession, clearSession],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
